import { useQuery } from "@tanstack/react-query";
import { ChartColumn, Info } from "lucide-react";
import { useState } from "react";
import { Link, useOutletContext } from "react-router";
import { api } from "../../api/client";
import { Alert } from "../../components/ui";
import type { ShellContext } from "../AppShell";

// "Statistici" (prototype page `stats`): how many times the public menu was opened — aggregated, without
// following individual visitors. Periods Azi / 7 zile / 30 de zile / Personalizat (≤ 92 days), the prototype's
// KPIs and empty states, a bar chart with its data as a table. Real data only (no estimates).

interface Stats {
  from: string;
  to: string;
  live: boolean;
  days: { day: string; views: number; qr: number; en: number }[];
  hours: { hour: number; views: number; qr: number }[] | null;
  period: { views: number; qr: number; en: number };
  previous: { from: string; to: string; views: number } | null;
  total: { views: number; since: string | null };
}

const PERIODS: [string, string][] = [
  ["1", "Azi"],
  ["7", "7 zile"],
  ["30", "30 de zile"],
  ["custom", "Personalizat"],
];
const iso = (d: Date) => d.toISOString().slice(0, 10);
const todayIso = () => {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Bucharest", dateStyle: "short" }).format(
    new Date(),
  );
  return p;
};
const addDays = (s: string, n: number) => iso(new Date(Date.parse(`${s}T00:00:00Z`) + n * 86_400_000));
const nInt = (n: number) => n.toLocaleString("ro-RO");
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const fmt = (s: string, o: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" }) =>
  new Date(`${s}T12:00:00Z`).toLocaleDateString("ro-RO", o);

export function StatsPage() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const today = todayIso();
  const [period, setPeriod] = useState("7");
  const [custom, setCustom] = useState({ from: addDays(today, -13), to: today });
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);

  let from = today;
  let to = today;
  let err = "";
  if (period === "7") from = addDays(today, -6);
  else if (period === "30") from = addDays(today, -29);
  else if (period === "custom") {
    ({ from, to } = custom);
    if (!from || !to) err = "Alege ambele date.";
    else if (from > to) err = "Data de început trebuie să fie înaintea datei de sfârșit.";
    else if (to > today) err = "Data de sfârșit nu poate fi în viitor.";
    else if ((Date.parse(to) - Date.parse(from)) / 86_400_000 > 91)
      err = "Alege o perioadă de cel mult 92 de zile.";
  }
  const q = useQuery({
    queryKey: ["stats", rid, from, to],
    enabled: !err,
    queryFn: () => api<Stats>("GET", `/restaurants/${rid}/stats?from=${from}&to=${to}`),
  });
  const d = q.data;
  const points = d
    ? d.hours
      ? d.hours.map((h) => ({
          v: h.views,
          q: h.qr,
          lab: `${String(h.hour).padStart(2, "0")}:00`,
          short: h.hour % 3 === 0 ? String(h.hour).padStart(2, "0") : "",
        }))
      : d.days.map((x, i) => ({
          v: x.views,
          q: x.qr,
          lab: fmt(x.day, { weekday: "short", day: "numeric", month: "short" }),
          short:
            d.days.length <= 7
              ? fmt(x.day, { weekday: "short" })
              : i % (d.days.length <= 31 ? 5 : Math.ceil(d.days.length / 6)) === 0
                ? fmt(x.day)
                : "",
        }))
    : [];
  const max = Math.max(1, ...points.map((p) => p.v));
  const delta = d?.previous?.views
    ? Math.round(((d.period.views - d.previous.views) / d.previous.views) * 100)
    : null;
  const cmp =
    period === "1"
      ? "ieri"
      : d?.previous
        ? `perioada anterioară (${fmt(d.previous.from)} – ${fmt(d.previous.to)})`
        : "";

  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        <h1 className="h1">Statistici</h1>
        <p className="page-sub" style={{ margin: 0, maxWidth: 640 }}>
          Câte ori a fost deschis meniul public. Date agregate, fără urmărirea vizitatorilor individuali.
        </p>
      </div>
      <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div className="row" style={{ gap: 4, flexWrap: "wrap" }}>
          {PERIODS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              aria-pressed={period === k}
              className={`btn btn-sm ${period === k ? "btn-primary" : "btn-secondary"}`}
              onClick={() => {
                setPeriod(k);
                setHover(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="row" style={{ gap: 8 }}>
            <label className="stack" style={{ gap: 2 }}>
              <span className="field-label">De la</span>
              <input
                type="date"
                className="field-input"
                value={custom.from}
                max={today}
                onChange={(e) => setCustom({ ...custom, from: e.target.value })}
              />
            </label>
            <label className="stack" style={{ gap: 2 }}>
              <span className="field-label">Până la</span>
              <input
                type="date"
                className="field-input"
                value={custom.to}
                max={today}
                onChange={(e) => setCustom({ ...custom, to: e.target.value })}
              />
            </label>
          </div>
        )}
      </div>
      {err && <Alert tone="error">{err}</Alert>}
      {q.isPending && (
        <p className="muted" role="status" style={{ margin: 0 }}>
          Se încarcă statisticile…
        </p>
      )}
      {q.isError && <Alert tone="error">{q.error.message}</Alert>}
      {d && !d.live && d.total.views === 0 ? (
        <section className="card empty">
          <ChartColumn size={28} aria-hidden="true" />
          <strong>Încă nu ai un meniu publicat</strong>
          <p style={{ margin: 0 }}>
            Statisticile apar după ce publici meniul și clienții îl deschid. Nu afișăm date estimate.
          </p>
          <Link className="btn btn-secondary btn-sm" to="/app/products">
            Mergi la produse
          </Link>
        </section>
      ) : d && d.total.views === 0 ? (
        <section className="card empty">
          <ChartColumn size={28} aria-hidden="true" />
          <strong>Meniul nu are încă vizite</strong>
          <p style={{ margin: 0 }}>
            Meniul este publicat, dar nu a fost deschis în perioada aleasă. Pune codurile QR pe mese ca să
            apară primele deschideri.
          </p>
          <Link className="btn btn-secondary btn-sm" to="/app/qr">
            Descarcă codul QR
          </Link>
        </section>
      ) : d ? (
        <>
          <section className="card" aria-label="Indicatori">
            <div className="stat-grid">
              <div className="stat">
                <span className="k">Deschideri în perioadă</span>
                <span className="v">{nInt(d.period.views)}</span>
                <span className="caption muted">
                  {delta === null
                    ? "Comparație indisponibilă: nu avem destule date pentru perioada anterioară."
                    : `${delta >= 0 ? `↑ ${delta}` : `↓ ${Math.abs(delta)}`}% față de ${cmp}`}
                </span>
              </div>
              <div className="stat">
                <span className="k">Prin scanarea codului QR</span>
                <span className="v">{nInt(d.period.qr)}</span>
                <span className="caption muted">{pct(d.period.qr, d.period.views)}% din deschideri</span>
              </div>
              <div className="stat">
                <span className="k">În limba engleză</span>
                <span className="v">{pct(d.period.en, d.period.views)}%</span>
                <span className="caption muted">{nInt(d.period.en)} deschideri</span>
              </div>
              <div className="stat">
                <span className="k">Total înregistrat</span>
                <span className="v">{nInt(d.total.views)}</span>
                <span className="caption muted">
                  {d.total.since
                    ? `de la ${fmt(d.total.since, { day: "numeric", month: "long", year: "numeric" })}`
                    : ""}
                </span>
              </div>
            </div>
          </section>
          <section className="card stack" style={{ gap: 12 }} aria-labelledby="st-ch">
            <h2 id="st-ch" className="h2">
              {d.hours ? "Deschideri azi, pe ore" : "Deschideri pe zile"}
            </h2>
            <div
              role="img"
              aria-label={`Grafic cu ${nInt(d.period.views)} deschideri între ${fmt(d.from)} și ${fmt(d.to)}. Maximum: ${nInt(max)}. Datele sunt disponibile și ca tabel.`}
              className="stats-chart"
              onMouseLeave={() => setHover(null)}
              onMouseMove={(e) => {
                const i = (e.target as HTMLElement).closest("[data-i]")?.getAttribute("data-i");
                if (i) setHover(Number(i));
              }}
              style={{ gap: points.length > 31 ? 1 : points.length > 7 ? 2 : 8 }}
            >
              {points.map((p, i) => (
                <div key={p.lab} className="stats-col" data-i={i}>
                  <div className="stats-bar-wrap">
                    <div
                      className="stats-bar"
                      style={{
                        height: `${p.v ? Math.max(2, Math.round((p.v / max) * 100)) : 0}%`,
                        background: hover === i ? "#2A1B4D" : "rgba(42, 27, 77, 0.72)",
                      }}
                    />
                  </div>
                  <span className="stats-x">{p.short}</span>
                </div>
              ))}
            </div>
            <span className="caption muted" aria-live="polite">
              <Info size={14} aria-hidden="true" />{" "}
              {hover !== null && points[hover]
                ? `${points[hover].lab} · ${nInt(points[hover].v)} deschideri, dintre care ${nInt(points[hover].q)} prin QR`
                : "Treci peste o bară pentru detalii sau deschide tabelul de date."}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ alignSelf: "flex-start" }}
              aria-expanded={table}
              onClick={() => setTable(!table)}
            >
              {table ? "Ascunde tabelul" : "Vezi datele ca tabel"}
            </button>
            {table && (
              <table className="stats-table">
                <thead>
                  <tr>
                    <th scope="col">{d.hours ? "Ora" : "Ziua"}</th>
                    <th scope="col">Deschideri</th>
                    <th scope="col">Prin QR</th>
                  </tr>
                </thead>
                <tbody>
                  {points.map((p) => (
                    <tr key={p.lab}>
                      <td>{p.lab}</td>
                      <td>{nInt(p.v)}</td>
                      <td>{nInt(p.q)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      ) : null}
    </>
  );
}
